"use client";

import React from "react";
import { ArrowRight, ShieldCheck } from "lucide-react";
import { Link } from "@/components/common/Link";
import { Button } from "@/components/ui/button";
import { PostsProvider, usePosts } from "@/integrations/wordpress/WordPressPostsProvider";
import { WP_Query } from "@/integrations/wordpress/wp_query";
const TARGET_PROJECT_TITLES = ["Large Commercial Building Scaffolding", "Large Property with Temporary Roof", "New Build Development Scaffolding", "Residential Roof Replacement Scaffolding", "Heritage Building Scaffolding", "Commercial Office Refurbishment Access", "Terraced & Townhouse Scaffolding", "Marine / Boat Scaffolding"];
function GalleryConsumer() {
  const {
    posts,
    loading,
    error,
    refetch
  } = usePosts();
  if (loading) {
    return <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 lg:gap-8">
        {[...Array(6)].map((_, i) => <div key={i} data-index={i} className="flex flex-col rounded-lg bg-card border border-border overflow-hidden shadow-xs animate-pulse">
            <div className="aspect-[16/10] w-full bg-muted" />
            <div className="p-5 sm:p-6 space-y-3">
              <div className="h-4 w-24 bg-muted rounded-md" />
              <div className="h-6 w-3/4 bg-muted rounded-md" />
              <div className="h-4 w-full bg-muted rounded-md" />
              <div className="h-4 w-5/6 bg-muted rounded-md" />
            </div>
          </div>)}
      </div>;
  }
  if (error) {
    return <div className="rounded-lg border border-border bg-card p-6 text-center text-muted-foreground">
        <p className="text-sm">Unable to load project gallery.</p>
        <Button variant="outline" size="sm" onClick={() => refetch()} className="mt-3">
          Retry
        </Button>
      </div>;
  }
  const displayedProjects = TARGET_PROJECT_TITLES.map(targetTitle => posts.find((p: any) => {
    const acf = (p as any).acf || p.customFields || {};
    return acf.project_title === targetTitle;
  })).filter((p): p is (typeof posts)[0] => Boolean(p));
  const itemsToRender = displayedProjects.length > 0 ? displayedProjects : posts.slice(0, 8);
  if (itemsToRender.length === 0) {
    return null;
  }
  return <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 lg:gap-8">
      {itemsToRender.map((post, index) => {
      const acf = (post as any).acf || post.customFields || {};
      const title = acf.project_title || "";
      const category = acf.category || "";
      const summary = acf.summary || "";
      const rawImg = acf.image;
      const imageSrc = typeof rawImg === "string" ? rawImg : rawImg?.url || rawImg?.src || "";
      const imageObj = {
        src: imageSrc,
        srcset: "",
        sizes: ""
      };
      return <article key={post.id || index} data-index={index} className="relative flex flex-col rounded-lg bg-card border border-border overflow-hidden shadow-xs">
            {/* Image Frame */}
            <div className="relative aspect-[16/10] w-full overflow-hidden bg-muted">
              {imageObj.src ? <img src={imageObj.src} data-wvc-srcset={imageObj.srcset} data-wvc-sizes={imageObj.sizes} alt={title} loading="lazy" decoding="async" className="h-full w-full object-cover object-center" /> : <div className="h-full w-full bg-muted flex items-center justify-center text-muted-foreground text-xs">
                  BMF Scaffolding
                </div>}
              {category && <div className="absolute top-3 left-3">
                  <span className="inline-flex items-center gap-1.5 rounded-md bg-secondary/90 backdrop-blur-xs px-2.5 py-1 text-xs font-semibold text-secondary-foreground">
                    <ShieldCheck className="size-3.5 text-primary" aria-hidden="true" />
                    {category}
                  </span>
                </div>}
            </div>

            {/* Card Body */}
            <div className="flex flex-1 flex-col justify-between p-5 sm:p-6">
              <div>
                <h3 className="text-lg font-bold text-card-foreground font-default leading-snug">
                  {title}
                </h3>
                {summary && <p className="mt-2 text-sm text-muted-foreground font-default leading-relaxed">
                    {summary}
                  </p>}
              </div>
            </div>
          </article>;
    })}
    </div>;
}
export default function Gallery() {
  const wpQuery = new WP_Query({
    post_type: "project",
    per_page: 100
  });
  return <section id="project-gallery" data-nav="light" className="relative bg-background py-16 md:py-24 lg:py-28 border-b border-border">
      <div className="container mx-auto px-5 sm:px-6 lg:px-8 max-w-7xl">
        {/* Header Block with Datum rule */}
        <div className="max-w-3xl mb-12 md:mb-16">
          <div className="inline-flex items-center gap-2 mb-3">
            <span className="h-1 w-8 bg-primary rounded-lg" aria-hidden="true" />
            <span className="text-xs md:text-sm font-bold uppercase tracking-wider text-muted-foreground font-default">
              Field Documentation
            </span>
          </div>
          <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold tracking-tight text-foreground font-default leading-tight">
            Recent site work across the South East
          </h2>
          <p className="mt-4 text-base sm:text-lg text-muted-foreground font-default leading-relaxed">
            Take a look at how our teams configure compliant access for commercial developments, residential builds, temporary roofs, and solar installs.
          </p>
        </div>

        {/* Gallery Grid via PostsProvider */}
        <PostsProvider wp_query={{
        post_type: "project",
        per_page: 100,
        status: ["publish"]
      }}>
          <GalleryConsumer />
        </PostsProvider>

        {/* Bottom CTA Block */}
        <div className="mt-12 md:mt-16 flex flex-col sm:flex-row items-center justify-between gap-6 rounded-lg bg-card border border-border p-6 sm:p-8 shadow-xs">
          <div className="max-w-xl text-center sm:text-left">
            <h3 className="text-lg sm:text-xl font-bold text-card-foreground font-default">
              Need scaffolding configured for an upcoming project?
            </h3>
            <p className="mt-1 text-sm text-muted-foreground font-default">
              We provide prompt site surveys, NASC-compliant designs, and clear quotation packages.
            </p>
          </div>
          <div className="flex flex-wrap items-center justify-center sm:justify-end gap-3 shrink-0 w-full sm:w-auto">
            <Button asChild className="h-11 px-6 font-semibold shadow-xs">
              <Link to="/ProjectsCaseStudiesPage">
                View All Projects
                <ArrowRight className="size-4 ml-1.5" aria-hidden="true" />
              </Link>
            </Button>
            <Button asChild variant="outline" className="h-11 px-5 font-semibold">
              <Link to="/ContactRequestQuotePage">
                Request a quote
              </Link>
            </Button>
          </div>
        </div>
      </div>
    </section>;
}